import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { PageHeader, Badge } from "../../components/ui-extras";
import PortalBanner from "../../components/PortalBanner";
import { useAuth } from "../../lib/auth";
import { Download, FileCsv, FilePdf } from "@phosphor-icons/react";
import { priceListCountriesFor } from "../../lib/priceListCapability";

/**
 * Customer Portal — Price lists.
 *
 * P4-N5B SLICE C6 — Canonical Network + Country selectors.
 * ─────────────────────────────────────────────────────────
 * The filter tile presents a canonical two-control surface:
 *   1. Network   (All networks · Esso · Pipeline · Petro-Pass)
 *   2. Country   (Canada · USA — dataset-scoped by
 *                 `priceListCountriesFor` AND customer authorization)
 *
 * Price-list capability is INTENTIONALLY DIFFERENT from transaction
 * capability: Esso and Pipeline price-lists are Canada-only; Petro-Pass
 * price-lists cover Canada + USA. This surface must therefore NOT
 * reuse the C1/C5 transaction-capability helper (which widens Esso →
 * {CA, US}) — see `frontend/src/lib/priceListCapability.js`.
 *
 * Wire contract is UNCHANGED from the pre-C6 release — the compound
 * UI tokens are gone but the emitted params are byte-identical:
 *   Esso        → { network: "esso" }                (implicit CA)
 *   Pipeline    → { network: "pipeline" }            (implicit CA)
 *   Petro-Pass  → { network: "petro_pass", country: "CA" | "US" }
 *   All networks→ { }                                 (no filter)
 *
 * Backend authorization gates remain authoritative — an unauthorized
 * URL call still returns 403 (see `resolve_authorized_price_report_lanes`
 * in `routes/customers.py`). The frontend hides ineligible controls so
 * the customer never authors an impossible request.
 *
 * Data source alignment:
 *   GET /api/customers/{customer_id}/price-report?date_from=YYYY-MM-DD
 *                                                &date_to=YYYY-MM-DD
 *                                                &network=<opt>
 *                                                &country=<opt>
 *
 * This is the SAME endpoint that powers Admin → "View price report"
 * and the portal CSV/PDF downloads. For a matching customer + station +
 * product + effective_date, the numbers rendered below MUST match the
 * admin's price-report view cell-for-cell. No independent pricing/tax
 * math happens on this page.
 */

const _today = () => new Date().toISOString().slice(0, 10);

// Client-side filter for a text search across the customer-facing
// location fields the operator sees in the table. Case-insensitive
// substring match; empty query passes everything.
// Phase CUSTOMER-PORTAL-PP-PRICE-LISTS-EXACT-DATE-IMPL (Feb 2026):
// Client-side effective-date range filter. The backend price-report
// endpoint intentionally applies carry-forward semantics for PP-CA
// and PP-US (a price effective before `on_date` stays applicable until
// superseded) so downloadable/emailed/admin reports always show the
// currently-applicable price. The interactive portal table, however,
// should reflect the exact effective-date range the customer picked:
// selecting a future date with no upload yet should show "No price
// entries match your filters." rather than yesterday's carry-forward.
// Rows carry `EFFECTIVE DATE` as an ISO `YYYY-MM-DD` string, so
// lexicographic inclusive comparison is sufficient. CSV/PDF/emailed
// paths are untouched — this filter is presentation-only.
function _inRange(row, from, to) {
  const effectiveDate = row?.["EFFECTIVE DATE"];
  if (!effectiveDate) return false;
  return effectiveDate >= from && effectiveDate <= to;
}

function _matchesSearch(row, q) {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const hay = [
    row["SITE NUMBER"],
    row["NAME"],
    row["LOCATION"],
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(needle);
}

export default function CustomerPriceLists() {
  const { user } = useAuth();
  const customerId = user?.customer_id;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [dateFrom, setDateFrom] = useState(_today());
  const [dateTo, setDateTo] = useState(_today());
  // C6 canonical two-state selector — network + country. Never compound.
  const [network, setNetwork] = useState("");
  const [country, setCountry] = useState("");
  const [product, setProduct] = useState("");
  const [province, setProvince] = useState("");
  const [search, setSearch] = useState("");

  // Fetch customer profile so the frontend can hide lanes the
  // logged-in customer isn't authorized for. Backend remains
  // authoritative — an unauthorized URL call still returns 403.
  const [customerProfile, setCustomerProfile] = useState(null);
  useEffect(() => {
    if (!customerId) return;
    api.get(`/customers/${customerId}`)
      .then((res) => setCustomerProfile((res && res.data) || {}))
      .catch(() => setCustomerProfile({}));
  }, [customerId]);

  // ── Customer-authorized visibility gates ───────────────────────────
  // Legacy (Esso / Pipeline) lanes are declared per-customer via the
  // `price_report_networks` array (defaults to ["esso","pipeline"]).
  // Petro-Pass CA / US lanes live in the newer `authorized_lanes`
  // structure and MUST NOT be inferred from the legacy array. Same
  // rule as the pre-C6 implementation.
  const priceReportNetworks = useMemo(() => {
    const raw = (customerProfile && customerProfile.price_report_networks)
      || ["esso", "pipeline"];
    return raw
      .map((n) => (n || "").toLowerCase())
      .filter((n) => n === "esso" || n === "pipeline");
  }, [customerProfile]);

  const authorizedPPCountries = useMemo(() => {
    const lanes = (customerProfile && customerProfile.authorized_lanes) || [];
    const out = [];
    const hasCA = lanes.some(
      (l) => (l.network || "").toLowerCase() === "petro_pass"
             && (l.country || "").toUpperCase() === "CA",
    );
    const hasUS = lanes.some(
      (l) => (l.network || "").toLowerCase() === "petro_pass"
             && (l.country || "").toUpperCase() === "US",
    );
    if (hasCA) out.push("CA");
    if (hasUS) out.push("US");
    return out;
  }, [customerProfile]);

  // Canonical network list — order: Esso → Pipeline → Petro-Pass.
  // A network appears in the selector iff the customer holds at least
  // one authorized lane on that provider.
  const networks = useMemo(() => {
    const out = [];
    if (priceReportNetworks.includes("esso")) out.push("esso");
    if (priceReportNetworks.includes("pipeline")) out.push("pipeline");
    if (authorizedPPCountries.length > 0) out.push("petro_pass");
    return out;
  }, [priceReportNetworks, authorizedPPCountries]);

  // Country options for the currently-selected network — dataset
  // capability from `priceListCountriesFor` intersected with the
  // customer's authorization scope. For "" (All networks) there is
  // NO legal country combination — the control is locked.
  const _priceListCountriesFor = useCallback((net) => {
    if (!net) return [];
    const capabilityCountries = priceListCountriesFor(net);
    if (net === "petro_pass") {
      return capabilityCountries.filter((c) => authorizedPPCountries.includes(c));
    }
    return capabilityCountries.slice();
  }, [authorizedPPCountries]);

  const availableCountries = useMemo(
    () => _priceListCountriesFor(network),
    [_priceListCountriesFor, network],
  );

  // Network reconciler — when the network changes, clamp `country`
  // to a value that remains legal under the new capability slice.
  // This is the ONLY path that guarantees no impossible wire pair
  // (e.g. Esso-USA) can ever be authored via UI interaction.
  const onNetworkChange = useCallback((nextNetwork) => {
    setNetwork(nextNetwork);
    if (!nextNetwork) {
      setCountry("");
      return;
    }
    const legal = _priceListCountriesFor(nextNetwork);
    if (legal.length === 0) {
      setCountry("");
      return;
    }
    setCountry((prev) => (legal.includes(prev) ? prev : legal[0]));
  }, [_priceListCountriesFor]);

  const networkLabel = (v) => {
    if (v === "esso") return "Esso";
    if (v === "pipeline") return "Pipeline";
    if (v === "petro_pass") return "Petro-Pass";
    return (v || "").toUpperCase();
  };

  // Refetch when the customer changes network/country or the date
  // range. All other filters (product / province / search) filter
  // locally — the returned dataset is already customer-scoped.
  //
  // Feb 2026 — Auto-retry-once. Transient failures (single lost
  // packet, brief backend blip, a router / gateway restart) shouldn't
  // scare the user with an error banner. On the FIRST failure inside
  // a fetch cycle we wait 1 s and try again silently. Only the second
  // attempt's outcome decides whether the banner surfaces. When the
  // user clicks "Try again" from the banner we treat their click as
  // an explicit single attempt (no cascading auto-retry) so they get
  // immediate feedback.
  const fetchPrices = useCallback((opts = {}) => {
    const allowAutoRetry = opts.allowAutoRetry !== false;
    if (!customerId) {
      setLoading(false);
      return;
    }
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    setErrored(false);
    // Wire contract (byte-identical to pre-C6):
    //   Esso / Pipeline → network=<value>            (implicit CA on backend)
    //   Petro-Pass      → network=petro_pass&country=CA|US
    //   All networks    → (no network / no country)
    // Country is emitted ONLY when Network=Petro-Pass, preserving the
    // exact legacy request shape. This mirrors the pre-C6 `params`
    // construction so `resolve_authorized_price_report_lanes` and the
    // PP-US branch at `routes/customers.py:1225` behave identically.
    const params = { date_from: dateFrom, date_to: dateTo };
    if (network) params.network = network;
    if (network === "petro_pass" && country) params.country = country;
    const handleFailure = () => {
      if (allowAutoRetry) {
        // Keep loading=true so the UI shows a steady "Loading…" and
        // the auto-retry is genuinely invisible from the user's view.
        setTimeout(() => fetchPrices({ allowAutoRetry: false }), 1000);
        return;
      }
      // Any 401/500/network failure lands here on the second attempt.
      // Wipe the row buffer so a stale success doesn't linger on-
      // screen alongside the error banner, and flip the error flag.
      setRows([]);
      setErrored(true);
      setLoading(false);
    };
    api
      .get(`/customers/${customerId}/price-report`, { params })
      .then((res) => {
        // The shared axios interceptor at lib/api.js:61 silently maps
        // a GET 403 into `{data: [], _rbacDenied: true}` to keep other
        // pages quiet during Preview Mode. On this page a 403 is a
        // real failure that must surface a clear customer-safe error
        // — check the sentinel and route it through the same retry
        // path as thrown failures.
        if (res && res._rbacDenied) {
          handleFailure();
          return;
        }
        setRows((res.data && res.data.rows) || []);
        setErrored(false);
        setLoading(false);
      })
      .catch(handleFailure);
  }, [customerId, network, country, dateFrom, dateTo]);

  useEffect(() => {
    fetchPrices();
  }, [fetchPrices]);

  const provinces = useMemo(
    () => Array.from(new Set(rows.map((r) => r["PROV."]).filter(Boolean))).sort(),
    [rows],
  );
  const products = useMemo(
    () => Array.from(new Set(rows.map((r) => r.PRODUCT).filter(Boolean))).sort(),
    [rows],
  );

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (product && r.PRODUCT !== product) return false;
      if (province && r["PROV."] !== province) return false;
      if (!_matchesSearch(r, search)) return false;
      if (!_inRange(r, dateFrom, dateTo)) return false;
      return true;
    });
  }, [rows, product, province, search, dateFrom, dateTo]);

  // Price list export URLs — same trusted endpoints already used by
  // Customer Portal → Downloads (routes/customers.py). Bound to the
  // exact FROM/TO/network/country/product/province/search the customer
  // sees in the filter tile so the download and the on-screen table
  // share one row population. Wire contract is identical to the
  // pre-C6 baseline the R1 Downloads-page card verified byte-parity
  // against. Product/province/search are applied server-side to
  // already-computed trusted rows — zero pricing math duplication.
  // Client-supplied customer_id is not authoritative; backend
  // enforces `user.customer_id == customer_id` and lane authorization.
  const downloadHrefs = useMemo(() => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    if (!customerId) return { csv: "#", pdf: "#" };
    const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
    if (network) params.set("network", network);
    if (network === "petro_pass" && country) params.set("country", country);
    if (product) params.set("product", product);
    if (province) params.set("province", province);
    if (search && search.trim()) params.set("search", search.trim());
    const qs = params.toString();
    return {
      csv: `${backend}/api/customers/${customerId}/price-report/csv?${qs}`,
      pdf: `${backend}/api/customers/${customerId}/price-report/pdf?${qs}`,
    };
  }, [customerId, dateFrom, dateTo, network, country, product, province, search]);
  const downloadDisabled = !customerId || !dateFrom || !dateTo;

  if (!customerId) {
    return (
      <>
        <PageHeader
          title="Price lists"
          subtitle="PORTAL / PRICE LISTS"
          testId="portal-pricelists-header"
        />
        <div className="gf-card">
          <div
            className="gf-card-body text-sm text-zinc-600"
            data-testid="portal-pricelists-unlinked"
          >
            Your account isn&apos;t linked to a customer yet. Contact
            hello@getfuelcard.com to enable your price list.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Price lists"
        subtitle="PORTAL / PRICE LISTS"
        testId="portal-pricelists-header"
      />

      <PortalBanner variant="prices" />

      {/* Filter row — canonical two-control Network + Country surface
          (C6). Date-range strip retained above so ops can preview a
          specific effective date without touching the Downloads page.
          Defaults to today/today, so the loaded view matches the
          previous "today only" behavior. */}
      <div
        className="gf-card mb-4"
        data-testid="portal-pricelists-filters"
      >
        <div className="gf-card-body space-y-3">
          <div
            className="flex flex-wrap items-end gap-3"
            data-testid="portal-pricelists-daterange"
          >
            <div>
              <label className="overline text-[10px] block mb-1">FROM</label>
              <input
                type="date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => setDateFrom(e.target.value)}
                className="gf-input"
                data-testid="portal-pricelists-date-from"
              />
            </div>
            <div>
              <label className="overline text-[10px] block mb-1">TO</label>
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => setDateTo(e.target.value)}
                className="gf-input"
                data-testid="portal-pricelists-date-to"
              />
            </div>
            {(dateFrom !== _today() || dateTo !== _today()) && (
              <button
                type="button"
                className="text-xs text-zinc-500 hover:text-zinc-800 underline underline-offset-2 mb-2"
                onClick={() => {
                  const t = _today();
                  setDateFrom(t);
                  setDateTo(t);
                }}
                data-testid="portal-pricelists-date-today"
              >
                Today
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <select
              value={network}
              onChange={(e) => onNetworkChange(e.target.value)}
              className="gf-input"
              data-testid="portal-pricelists-network"
            >
              <option value="">All networks</option>
              {networks.map((n) => (
                <option key={n} value={n} data-testid={`portal-pricelists-network-opt-${n}`}>{networkLabel(n)}</option>
              ))}
            </select>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              disabled={!network || availableCountries.length <= 1}
              className="gf-input"
              data-testid="portal-pricelists-country"
            >
              {!network && (
                <option value="">All countries</option>
              )}
              {network && availableCountries.map((c) => (
                <option
                  key={c}
                  value={c}
                  data-testid={`portal-pricelists-country-opt-${c}`}
                >
                  {c === "CA" ? "Canada" : "USA"}
                </option>
              ))}
            </select>
            <select
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              className="gf-input"
              data-testid="portal-pricelists-product"
            >
              <option value="">All products</option>
              {products.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <select
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              className="gf-input"
              data-testid="portal-pricelists-province"
            >
              <option value="">All provinces</option>
              {provinces.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <input
              type="search"
              placeholder="Search station / city"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="gf-input"
              data-testid="portal-pricelists-search"
            />
          </div>

          {/* Download actions — embedded in the SAME filter tile per
              R1A. CSV/PDF hrefs bind to the tile's FROM/TO/network/
              country so downloads reflect exactly what the customer
              sees on screen. URL construction, endpoints, tenant
              enforcement and Petro-Pass CA/US authorization gating
              are identical to the R1 baseline the operator already
              accepted. */}
          <div
            className="flex flex-wrap items-center justify-end gap-2 pt-1"
            data-testid="portal-pricelists-downloads"
          >
            <span className="overline text-[10px] mr-1" style={{ color: "#6B7280" }}>
              DOWNLOAD
            </span>
            <a
              href={downloadDisabled ? undefined : downloadHrefs.csv}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={downloadDisabled}
              className="gf-btn gf-btn-outline inline-flex items-center gap-2 text-xs no-underline"
              style={downloadDisabled ? { opacity: 0.5, pointerEvents: "none" } : undefined}
              data-testid="portal-pricelists-download-csv"
            >
              <FileCsv size={16} weight="duotone" style={{ color: "#1A3FBA" }} />
              CSV
              <Download size={12} style={{ color: "#9CA3AF" }} />
            </a>
            <a
              href={downloadDisabled ? undefined : downloadHrefs.pdf}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={downloadDisabled}
              className="gf-btn gf-btn-outline inline-flex items-center gap-2 text-xs no-underline"
              style={downloadDisabled ? { opacity: 0.5, pointerEvents: "none" } : undefined}
              data-testid="portal-pricelists-download-pdf"
            >
              <FilePdf size={16} weight="duotone" style={{ color: "#1A3FBA" }} />
              PDF
              <Download size={12} style={{ color: "#9CA3AF" }} />
            </a>
          </div>
        </div>
      </div>

      {/* 10-column price table — sourced from the same customer-facing
          price-report endpoint used by Admin "View price report" and
          the portal CSV/PDF downloads. Horizontal scroll wrapper for
          narrow screens; column set never collapses.
          Phase 3E-1a: when Petro-Pass USA rows arrive (detected by
          `UOM==='GAL'` on the first row), we swap the CAD/L column
          set for a USD/GAL layout. Rows are never mixed across UOMs
          because the backend dispatches by network+country. */}
      <div className="gf-card">
        <div className="overflow-x-auto">
          {(() => {
            const isUsdGal = filtered.length > 0 && (filtered[0].UOM === "GAL" || filtered[0].CURRENCY === "USD");
            if (isUsdGal) {
              return (
                <table className="gf-table" data-testid="portal-pricelists-table">
                  <thead>
                    <tr>
                      <th>SITE NUMBER</th>
                      <th>NAME</th>
                      <th>LOCATION</th>
                      <th>STATE</th>
                      <th>PRODUCT</th>
                      <th className="text-right">PRICE/GAL.</th>
                      <th>CURRENCY</th>
                      <th>EFFECTIVE DATE</th>
                      <th>EFFECTIVE TIME</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={9} className="text-center text-zinc-400 py-10">Loading…</td></tr>
                    )}
                    {!loading && filtered.length === 0 && (
                      <tr><td colSpan={9} className="text-center text-zinc-400 py-10" data-testid="portal-pricelists-empty">No price entries.</td></tr>
                    )}
                    {!errored && filtered.slice(0, 2000).map((r, i) => (
                      <tr
                        key={`${r["SITE NUMBER"]}-${r.PRODUCT}-${r["EFFECTIVE DATE"]}-${r["EFFECTIVE TIME"] || ""}-${i}`}
                        data-testid={`portal-pricelists-row-${r["SITE NUMBER"]}-${r.PRODUCT}`}
                      >
                        <td className="font-mono text-xs">{r["SITE NUMBER"] || "—"}</td>
                        <td className="text-sm font-medium">{r.NAME || "—"}</td>
                        <td className="text-sm">{r.LOCATION || "—"}</td>
                        <td className="font-mono text-xs">{r.STATE || "—"}</td>
                        <td><Badge variant="info">{r.PRODUCT}</Badge></td>
                        <td className="gf-num font-mono text-right font-semibold">
                          {typeof r["PRICE/GAL."] === "number" ? r["PRICE/GAL."].toFixed(4) : "—"}
                        </td>
                        <td className="font-mono text-xs">{r.CURRENCY || "USD"}</td>
                        <td className="font-mono text-xs">{r["EFFECTIVE DATE"] || "—"}</td>
                        <td className="font-mono text-xs">{r["EFFECTIVE TIME"] || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            }
            return (
          <table className="gf-table" data-testid="portal-pricelists-table">
            <thead>
              <tr>
                <th>SITE NUMBER</th>
                <th>NAME</th>
                <th>LOCATION</th>
                <th>PROV.</th>
                <th>PRODUCT</th>
                <th className="text-right">PRICE/LTR.</th>
                <th className="text-right">GST/HST/FNT</th>
                <th className="text-right">PST/QST</th>
                <th className="text-right">TOTAL PRICE</th>
                <th>EFFECTIVE DATE</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={10} className="text-center text-zinc-400 py-10">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && errored && (
                <tr>
                  <td
                    colSpan={10}
                    className="py-10"
                    data-testid="portal-pricelists-error"
                  >
                    <div className="mx-auto max-w-md text-center">
                      <div className="text-sm text-rose-700 font-medium">
                        We couldn&apos;t load the price list right now.
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">
                        Please refresh the page or try again shortly.
                      </div>
                      <button
                        type="button"
                        onClick={() => fetchPrices({ allowAutoRetry: false })}
                        className="gf-btn gf-btn-outline mt-4 text-xs"
                        data-testid="portal-pricelists-retry"
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && !errored && filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    className="text-center text-zinc-400 py-10"
                    data-testid="portal-pricelists-empty"
                  >
                    No price entries match your filters.
                  </td>
                </tr>
              )}
              {!errored && filtered.slice(0, 2000).map((r, i) => (
                <tr
                  key={`${r["SITE NUMBER"]}-${r.PRODUCT}-${r["EFFECTIVE DATE"]}-${i}`}
                  data-testid={`portal-pricelists-row-${r["SITE NUMBER"]}-${r.PRODUCT}`}
                >
                  <td className="font-mono text-xs">{r["SITE NUMBER"] || "—"}</td>
                  <td className="text-sm font-medium">{r.NAME || "—"}</td>
                  <td className="text-sm">{r.LOCATION || "—"}</td>
                  <td className="font-mono text-xs">{r["PROV."] || "—"}</td>
                  <td>
                    <Badge variant="info">{r.PRODUCT}</Badge>
                  </td>
                  <td className="gf-num font-mono text-right">
                    {typeof r["PRICE/LTR."] === "number"
                      ? r["PRICE/LTR."].toFixed(4)
                      : "—"}
                  </td>
                  <td className="gf-num font-mono text-right">
                    {typeof r["GST/HST/FNT"] === "number"
                      ? r["GST/HST/FNT"].toFixed(4)
                      : "—"}
                  </td>
                  <td className="gf-num font-mono text-right">
                    {typeof r["PST/QST"] === "number"
                      ? r["PST/QST"].toFixed(4)
                      : "—"}
                  </td>
                  <td className="gf-num font-mono text-right font-semibold">
                    {typeof r["TOTAL PRICE"] === "number"
                      ? r["TOTAL PRICE"].toFixed(4)
                      : "—"}
                  </td>
                  <td className="font-mono text-xs">{r["EFFECTIVE DATE"] || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
            );
          })()}
        </div>
      </div>
    </>
  );
}
